// apps/fe/src/modules/catalog/screens/catalog-category-screen.tsx

'use client';
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { EventBus } from '@/shared/utils';
import { Catalog as CatalogDict } from '@/shared/i18n';
import { CatalogEvents, CatalogEventKeys } from '../events';
import { useCategory } from '../hooks/use-category';
import { CatalogCategoryInitialData, CategoryTypes } from '../types/catalog-category-data';
import BaseCatalogPage from '../ui/pages/base-catalog-page';
import { useProfileCatalogData } from '../hooks/use-profile-catalog-data';
import { useAuthStore } from '@/auth';

interface CatalogCategoryScreenProps {
  dict: CatalogDict;
  eventBus: EventBus<CatalogEvents>;
  initialData: CatalogCategoryInitialData;
  categoryName?: CategoryTypes;
  isLoggedIn: boolean;
}

/**
 * Category-specific catalog screen.
 * Shows courses filtered by a specific category.
 *
 * Memoized to prevent unnecessary renders
 */
const CatalogCategoryScreen = React.memo(function CatalogCategoryScreen({
  dict,
  eventBus,
  initialData,
  categoryName,
  isLoggedIn,
}: CatalogCategoryScreenProps) {
  const { user } = useAuthStore();

  // Performance tracking - uncomment for debugging
  // const renderCount = useRef(0);
  // useEffect(() => {
  //   renderCount.current += 1;
  //   console.log(`Category screen render count: ${renderCount.current}`);
  // });

  // Memoize the catalog params to avoid new references on each render
  const catalogParams = useMemo(() => ({ categoryName }), [categoryName]);

  // Memoize filter data from SSG to always be available immediately
  const filtersData = useMemo(() => initialData.filters, [initialData.filters]);

  // Rehydrate category data with user-specific information if logged in
  const {
    data: enhancedData,
    loading: loadingUserData,
    error: userDataError,
    hasUserData,
    updateFavoriteStatus,
  } = useProfileCatalogData({
    dataType: 'category',
    initialData,
    catalogParams,
    eventBus,
  });

  // Memoize derived category data to prevent recreation on every render
  const categoryData = useMemo(() => {
    if (user) {
      if (loadingUserData || !hasUserData) {
        return {
          filters: filtersData,
          category: { courses: [] },
          title: initialData.title,
        };
      }

      return {
        ...enhancedData,
        filters: filtersData,
      };
    }

    return {
      ...initialData,
      filters: filtersData,
    };
  }, [user, loadingUserData, hasUserData, enhancedData, initialData, filtersData]);

  // Use the category hook with memoized dependencies
  const category = useCategory({
    eventBus,
    filters: categoryData.filters,
    categoryName,
    courses: categoryData.category?.courses,
    withAuthentication: !!user,
  });

  // Memoize the loading state to prevent redundant recalculations
  const isLoading = useMemo(() => {
    if (user) {
      return loadingUserData || !hasUserData || category.loadingCategoryCatalogData;
    }
    return category.loadingCategoryCatalogData;
  }, [user, loadingUserData, hasUserData, category.loadingCategoryCatalogData]);

  // Memoize the favorite change handler to prevent recreation on each render
  const handleFavoriteChange = useCallback(
    (courseId: string, favouriteId: string | undefined) => {
      if (updateFavoriteStatus) {
        console.log('Updating favorite in category data:', courseId, favouriteId);
        updateFavoriteStatus(courseId, favouriteId);
      }
    },
    [updateFavoriteStatus],
  );

  // Emit the view event to signal the category view is ready - only once on mount
  useEffect(() => {
    eventBus.emit(CatalogEventKeys.category.view, undefined);
  }, [eventBus]);

  return (
    <BaseCatalogPage
      dict={dict}
      eventBus={eventBus}
      isLoggedIn={isLoggedIn}
      filteredItems={category.filteredCategoryItems}
      loadingCatalogData={isLoading}
      filteringCatalogData={true}
      activeFilters={category.categoryFilters}
      parentInstruments={category.parentCategoryInstruments}
      filters={categoryData.filters}
      pageTitle={categoryData.title}
      showBackButton={true}
      lastCourseElementRef={category.lastCourseElementRef}
      instrumentsFilterOnChange={category.instrumentsCategoryFilterOnChange}
      handleSkillLevelsChange={category.handleSkillLevelsChange}
      handleGenresChange={category.handleGenresChange}
      handleTopicsChange={category.handleTopicsChange}
      handleTypeChange={category.handleTypeChange}
      onFavoriteChange={handleFavoriteChange}
      isEnrichingData={loadingUserData}
      useSkeleton={true} // Always use skeleton loading
      onResetSidebarFilters={category.onResetSidebarFilters}
      isAnyFilterActive={category.isAnyFilterActive}
    />
  );
});

export default CatalogCategoryScreen;
